import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Hero from '../content/Hero';
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import Triple from '../content/Triple';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import CorporateFaq from '../faqs/CorporateFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';

function CorporateCatering() {
  const { vehicle_type, site_title, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Corporate Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/corporate-catering'
        image='https://static.seattlefoodtruck.com/photos/home5.png'
        description={`Book corporate food ${vehicleType} catering in ${city_name} for office lunches, team events, and employee appreciation. Get free quotes from top local vendors.`}
        structuredData={{
          cateringService: {
            name: `Corporate Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book corporate food ${vehicleType} catering in ${city_name} for office lunches, team events, and employee appreciation.`,
            url: '/food-truck-catering/corporate-catering',
            serviceType: 'Corporate Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Corporate Catering', item: '/food-truck-catering/corporate-catering' }
            ]
          }
        }}
      />

      <Hero photo='https://static.seattlefoodtruck.com/photos/home5.png'>
        <h1>Corporate Food {vehicle_type} Catering</h1>
        <p>Reward your employees with an office lunch from a gourmet kitchen on wheels! Use our free request form below for quotes from {city_name}&#39;s best food {vehicleType}s.</p>
        <Link to='/catering/register' className='Button'>
          Get Started
        </Link>
      </Hero>

      <section className='Section'>
        <h3 className='u-phosphate'>How It Works</h3>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks1.webp'
            title='1. Tell Us About Your Event'
            text={`Answer a few questions about your upcoming event, and we'll reach out to over 500 of ${city_name}'s best food ${vehicleType}s.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks2.webp'
            title='2. Compare Quotes'
            text={`Read reviews, pricing and menus from ${city_name}'s best food ${vehicleType}s. Message directly with vendors via our online platform.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks3.webp'
            title='3. Book Your Favorite'
            text={`Book your food ${vehicleType} knowing you're getting the best deal, with no added service fees or commission.`}
          />
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/OfficeCater1.jpeg' />
          <div className={styles.Cta_copy}>
            <h4>Ditch The Boring Office Catering</h4>

            <p>With {site_title} you&#39;ll get access to over 500 of {city_name}&#39;s best food {vehicleType}s to help treat your employees to made-to-order catering right on site! Guests can order at the truck or have the food set up inside as our vendors can accommodate any venue. Whether it&#39;s delicious <Link to='/food-truck-catering/pizza-catering'>deep-dish pizza</Link>, spicy <Link to='/food-truck-catering/asian-catering'>Asian curry</Link>, or fresh <Link to='/food-truck-catering/taco-catering'>tacos</Link>, our vendors can create an office catering experience that guests will love. Need a <Link to='/food-truck-catering/office-lunch'>recurring lunch program</Link>? We can set that up too.</p>

            <div>
              <Link to='/catering/register' className='Button'>
                Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta + ' ' + styles.Cta__reversed}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/OfficeCater2.jpeg' />
          <div className={styles.Cta_copy}>
            <h4>We&#39;ll handle the hard work</h4>

            <p>No more wasting time reaching out to multiple restaurants, waiting for a call back on their timeline. Send a request for your upcoming event and we&#39;ll contact {city_name}&#39;s best food {vehicleType}s on your behalf. Available vendors will respond via our platform with menu options, pricing, and details. You&#39;ll be able to compare quotes, read vendor reviews, and choose your favorite food {vehicleType} from our easy-to-use catering dashboard, all in a couple of hours, not days!</p>

            <div>
              <Link to='/catering/register' className='Button'>
                Book a Food {vehicle_type}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FeaturedTrucks />

      <CostEstimator title='Office Catering Cost Estimator' />

      <section className='Section--extraPaddingTop u-textCenter'>
        <h3 className='u-phosphate u-mb2'>Our Happy Customers</h3>
        <div className={styles.CustomerGrid}>
          <div>
            <div className={styles.CateringCustomerImg} style={{ backgroundImage: 'url(https://static.seattlefoodtruck.com/photos/Praveen.jpeg)' }} />
            <h4>Praveen - Redmond, WA</h4>
            <p>Our team was over the basic boxed lunch catering. We hired a different food {vehicleType} each Friday to give our employees something new each week parked right outside the office.</p>
          </div>
          <div>
            <div className={styles.CateringCustomerImg} style={{ backgroundImage: 'url(https://static.seattlefoodtruck.com/photos/Dana.jpeg)' }} />
            <h4>Dana - Kent, WA</h4>
            <p>I wanted to make sure our office lunch program was on auto-pilot. With a simple request, we received quotes from dozens of vendors bidding for our events. So simple to use!</p>
          </div>
          <div>
            <div className={styles.CateringCustomerImg} style={{ backgroundImage: 'url(https://static.seattlefoodtruck.com/photos/Heidi.jpeg)' }} />
            <h4>Heidi - Bothell, WA</h4>
            <p>Quick response time from so many vendors. We were able to work directly with each bid to narrow down prices, menus, and dietary restrictions. Highly recommended!</p>
          </div>
          <div>
            <div className={styles.CateringCustomerImg} style={{ backgroundImage: 'url(https://static.seattlefoodtruck.com/photos/Khea.jpeg)' }} />
            <h4>Khea - Seattle, WA</h4>
            <p>Food {vehicleType}s were a perfect way to treat our team. They arrived on time, set up in our parking lot, and left with no mess in a couple of hours.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <CorporateFaq />
      </section>

      <section className='Section'>
        <div className={styles.BookingCta}>
          <h3>Think of us for your next event</h3>
          <p>Free no-obligation estimates from {city_name}&#39;s best food {vehicleType}s.</p>
          <Link
            className='Button'
            to='/catering/register'
          >
            Food {vehicle_type} Catering
          </Link>
        </div>
      </section>
    </div>
  );
}

export default CorporateCatering;
